import React, { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Timer, Trophy, Swords, Copy, Link2, Check, RotateCw } from 'lucide-react'
import { VersusHeader } from '../../components/VersusHeader.jsx'
import { profilePhoto } from '../../components/Avatar.jsx'
import { QCATS } from './questions.js'
import {
  buildMatch,
  makeRival,
  simulateRival,
  encodeChallenge,
  decodeChallenge,
  MATCH_LENGTH,
} from './engine.js'
import { load, update, awardBp, eloDelta } from '../lib/store.js'

const newSeed = () => Math.floor(Math.random() * 1e9)
const QUESTION_SECONDS = 20

export function DueloGame({ onBack, onComplete, session }) {
  const [phase, setPhase] = useState('menu') // menu | match | result | accept
  const [categoryId, setCategoryId] = useState('geografia')
  const [mode, setMode] = useState('quick')
  const [seed, setSeed] = useState(0)
  const [rival, setRival] = useState(null)
  const [friend, setFriend] = useState(null)
  const [index, setIndex] = useState(0)
  const [results, setResults] = useState([])
  const [locked, setLocked] = useState(null)
  const [seconds, setSeconds] = useState(QUESTION_SECONDS)
  const [codeInput, setCodeInput] = useState('')
  const [codeError, setCodeError] = useState('')
  const [copied, setCopied] = useState(false)
  const [elo, setElo] = useState(() => load().duelo.elo)

  const questions = useMemo(
    () => (seed ? buildMatch(seed, categoryId) : []),
    [seed, categoryId],
  )
  const current = questions[index]

  const me = {
    name: session?.name || session?.email?.split('@')[0] || 'Tú',
    src: session?.picture || profilePhoto(session?.name || 'viajero'),
    seed: session?.name,
    ring: '#22d3ee',
  }
  const foe = { name: rival?.name || 'Rival', src: rival ? profilePhoto(rival.name) : null, seed: rival?.name, ring: '#fb7185' }

  // intro clash, then the questions begin
  useEffect(() => {
    if (phase !== 'intro') return
    const t = setTimeout(() => setPhase('match'), 1700)
    return () => clearTimeout(t)
  }, [phase])

  // per-question countdown
  useEffect(() => {
    if (phase !== 'match' || locked != null || !current) return
    if (seconds <= 0) {
      answer(null)
      return
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [phase, seconds, locked, current])

  const beginMatch = ({ seed: s, cat, rival: r, modeName, friendData = null }) => {
    setSeed(s)
    setCategoryId(cat)
    setRival(r)
    setFriend(friendData)
    setMode(modeName)
    setIndex(0)
    setResults([])
    setLocked(null)
    setSeconds(QUESTION_SECONDS)
    setCopied(false)
    setPhase('intro')
  }

  const startQuick = () => {
    const s = newSeed()
    beginMatch({ seed: s, cat: categoryId, rival: makeRival(s, elo), modeName: 'quick' })
  }

  const answer = (choice) => {
    if (locked != null || !current) return
    const correct = choice === current.answerIndex
    setLocked({ correct, choice })
    setResults((r) => [...r, correct])
    setTimeout(() => {
      if (index + 1 >= MATCH_LENGTH) {
        finish([...results, correct])
      } else {
        setIndex((i) => i + 1)
        setLocked(null)
        setSeconds(QUESTION_SECONDS)
      }
    }, 650)
  }

  const finish = (allResults) => {
    const correct = allResults.filter(Boolean).length
    const rivalScore = mode === 'friend' && friend ? friend.score : simulateRival(seed, rival.elo)
    const scoreA = correct > rivalScore ? 1 : correct < rivalScore ? 0 : 0.5
    const won = scoreA === 1
    const draw = scoreA === 0.5

    let nextElo = elo
    if (mode === 'quick') {
      const rivalElo = rival.elo
      const delta = eloDelta(elo, rivalElo, scoreA)
      nextElo = elo + delta
      update((s) => {
        s.duelo.plays += 1
        s.duelo.elo = nextElo
        s.duelo.wins += won ? 1 : 0
        s.duelo.losses += !won && !draw ? 1 : 0
        s.duelo.draws += draw ? 1 : 0
        return s
      })
      setElo(nextElo)
    } else {
      update((s) => {
        s.duelo.plays += 1
        return s
      })
    }
    awardBp(correct * 12)
    onComplete?.(correct * 12)
    setResults(allResults)
    setPhase('result')
  }

  const acceptChallenge = () => {
    const decoded = decodeChallenge(codeInput)
    if (!decoded) {
      setCodeError('Código inválido. Copia el reto completo.')
      return
    }
    setCodeError('')
    beginMatch({
      seed: decoded.seed,
      cat: decoded.cat,
      rival: { name: decoded.name, elo: elo },
      modeName: 'friend',
      friendData: decoded,
    })
  }

  const shareCode = () => {
    const correct = results.filter(Boolean).length
    return encodeChallenge({
      seed,
      cat: categoryId,
      name: 'Tú',
      score: correct,
    })
  }

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(shareCode())
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  // ── MENU ────────────────────────────────────────────────────────────────
  if (phase === 'menu') {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
          >
            <ArrowLeft size={16} /> Volver
          </button>
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
            Duelo de Cultura
          </span>
        </div>

        <div className="p-4 rounded-2xl bg-gradient-to-br from-[#1a1408] to-[#0d0c08] border border-amber-900/40">
          <div className="text-2xl">⚔️</div>
          <h2 className="text-lg font-black text-white mt-1">Cultura general con rating</h2>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            {MATCH_LENGTH} preguntas. Tu Elo sube o baja según a quién enfrentas. Reta a un amigo
            con un código y jueguen la misma ronda.
          </p>
          <div className="flex items-center gap-3 mt-3">
            <span className="px-3 py-1.5 rounded-lg bg-amber-950/60 border border-amber-500/40 text-amber-300 text-sm font-black">
              Elo {elo}
            </span>
            <span className="text-[11px] text-slate-400">
              {load().duelo.wins}V · {load().duelo.losses}D · {load().duelo.draws}E
            </span>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">
            Categoría
          </div>
          <div className="grid grid-cols-3 gap-2">
            {QCATS.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategoryId(c.id)}
                className={`p-3 rounded-xl border text-center cursor-pointer transition-all ${
                  categoryId === c.id
                    ? 'border-amber-500/60 bg-amber-950/40'
                    : 'border-slate-800 bg-[#0e131f] hover:border-slate-700'
                }`}
              >
                <div className="text-lg">{c.emoji}</div>
                <div className="text-[11px] font-bold text-white mt-0.5">{c.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <button
            type="button"
            onClick={startQuick}
            className="p-4 rounded-2xl bg-amber-500 text-slate-950 font-black text-sm shadow-lg shadow-amber-500/25 active:scale-[0.99] cursor-pointer flex items-center justify-center gap-2"
          >
            <Swords size={18} /> Partida rápida
          </button>
          <button
            type="button"
            onClick={() => setPhase('accept')}
            className="p-4 rounded-2xl bg-[#131b29] border border-slate-700 text-white font-black text-sm active:scale-[0.99] cursor-pointer flex items-center justify-center gap-2"
          >
            <Link2 size={18} /> Aceptar reto de un amigo
          </button>
        </div>
      </div>
    )
  }

  // ── ACCEPT CHALLENGE ────────────────────────────────────────────────────
  if (phase === 'accept') {
    return (
      <div className="space-y-5">
        <button
          type="button"
          onClick={() => setPhase('menu')}
          className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
        >
          <ArrowLeft size={16} /> Volver
        </button>
        <h2 className="text-lg font-black text-white">Aceptar reto</h2>
        <p className="text-xs text-slate-400">
          Pega el código que te envió tu amigo. Jugarás exactamente las mismas preguntas y se
          comparan los aciertos.
        </p>
        <textarea
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value)}
          placeholder="CB1..."
          rows={3}
          className="w-full px-3 py-2.5 rounded-xl bg-[#0e131f] border border-slate-800 text-sm text-white outline-none focus:border-amber-500/50 font-mono"
        />
        {codeError && <p className="text-xs text-rose-400">{codeError}</p>}
        <button
          type="button"
          onClick={acceptChallenge}
          className="w-full py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm cursor-pointer"
        >
          Aceptar y jugar
        </button>
      </div>
    )
  }

  // ── INTRO ───────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    const cat = QCATS.find((c) => c.id === categoryId)
    return (
      <div className="relative min-h-[68vh] flex flex-col items-center justify-center gap-7 overflow-hidden">
        <span className="cbp-starfield" />
        <span className="cbp-vs-pop text-[11px] font-black uppercase tracking-[0.35em] text-amber-400/80">
          Duelo de Cultura
        </span>
        <VersusHeader me={me} foe={foe} size={150} variant="duelists" />
        <div className="cbp-vs-pop flex flex-col items-center gap-1">
          <span className="text-xs font-bold text-slate-400">
            {cat?.emoji} {cat?.label} · {MATCH_LENGTH} preguntas
          </span>
          <span className="text-sm font-black text-white animate-pulse">¡Prepárate!</span>
        </div>
      </div>
    )
  }

  // ── MATCH ───────────────────────────────────────────────────────────────
  if (phase === 'match' && current) {
    return (
      <div className="space-y-4 max-w-lg mx-auto">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setPhase('menu')}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
          >
            <ArrowLeft size={16} /> Salir
          </button>
          <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-950/70 border border-amber-500/40 text-amber-300 text-xs font-black">
            <Timer size={13} /> {seconds}s
          </span>
          <span className="text-xs font-bold text-slate-400">
            {index + 1}/{MATCH_LENGTH}
          </span>
        </div>

        {/* Face-off */}
        <div className="relative py-1">
          <VersusHeader
            me={{ ...me, sub: `${results.filter(Boolean).length} pts` }}
            foe={{ ...foe, sub: `${rival?.elo} Elo` }}
            size={58}
          />
          <div className="flex justify-center gap-1.5 mt-3">
            {Array.from({ length: MATCH_LENGTH }).map((_, i) => {
              const r = results[i]
              return (
                <span
                  key={i}
                  className={`w-2.5 h-2.5 rounded-full ${
                    r === true ? 'bg-emerald-400' : r === false ? 'bg-rose-500' : 'bg-slate-700'
                  }`}
                />
              )
            })}
          </div>
        </div>

        <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div
            className="h-full bg-amber-400 rounded-full transition-all"
            style={{ width: `${((index + 1) / MATCH_LENGTH) * 100}%` }}
          />
        </div>

        <div className="p-5 rounded-2xl bg-gradient-to-br from-[#141008] to-[#0c0b07] border border-slate-800">
          <p className="text-base font-bold text-white leading-snug">{current.q}</p>
        </div>

        <div className="space-y-2.5">
          {current.options.map((opt, i) => {
            const isAnswer = i === current.answerIndex
            const picked = locked?.choice === i
            const state =
              locked == null
                ? 'idle'
                : isAnswer
                  ? 'good'
                  : picked
                    ? 'bad'
                    : 'dim'
            const cls = {
              idle: 'bg-[#131a29] border-slate-700 hover:border-amber-500/60 text-white',
              good: 'bg-emerald-950/60 border-emerald-500/50 text-emerald-200',
              bad: 'bg-rose-950/60 border-rose-500/50 text-rose-200',
              dim: 'bg-[#0e131f] border-slate-800 text-slate-500',
            }[state]
            return (
              <button
                key={i}
                type="button"
                disabled={locked != null}
                onClick={() => answer(i)}
                className={`w-full text-left px-4 py-3.5 rounded-xl border text-sm font-bold transition-all cursor-pointer disabled:cursor-default ${cls}`}
              >
                {opt}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // ── RESULT ──────────────────────────────────────────────────────────────
  const correct = results.filter(Boolean).length
  const rivalScore = mode === 'friend' && friend ? friend.score : simulateRival(seed, rival?.elo || elo)
  const won = correct > rivalScore
  const draw = correct === rivalScore
  const isFriend = mode === 'friend'

  return (
    <div className="space-y-5 max-w-lg mx-auto">
      <div className="text-center space-y-4">
        <div
          className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center ${
            won ? 'bg-amber-500/20 border border-amber-500/50' : draw ? 'bg-slate-800' : 'bg-rose-950/50 border border-rose-500/40'
          }`}
        >
          <Trophy size={26} className={won ? 'text-amber-400' : draw ? 'text-slate-300' : 'text-rose-400'} />
        </div>

        <div className="relative py-2">
          <VersusHeader
            me={{ ...me, sub: `${correct} pts`, ring: won ? '#34d399' : '#22d3ee' }}
            foe={{ ...foe, sub: `${rivalScore} pts` }}
            size={120}
            variant="duelists"
          />
        </div>

        <h2 className="text-xl font-black text-white">
          {isFriend
            ? won
              ? `Le ganaste a ${friend.name}`
              : draw
                ? 'Empate'
                : `${friend.name} te ganó`
            : won
              ? '¡Victoria!'
              : draw
                ? 'Empate'
                : 'Derrota'}
        </h2>
        {!isFriend && (
          <p className="text-xs text-slate-400">
            Elo actual: <span className="font-black text-amber-300">{elo}</span>
          </p>
        )}
      </div>

      <div className="p-4 rounded-2xl bg-[#0e131f] border border-slate-800 space-y-3">
        <div className="text-[11px] font-black uppercase tracking-wider text-slate-400">
          Reta a un amigo
        </div>
        <p className="text-[11px] text-slate-400">
          Comparte este código. Jugarán las mismas {MATCH_LENGTH} preguntas y se comparan los
          aciertos.
        </p>
        <div className="px-3 py-2 rounded-lg bg-[#080b12] border border-slate-800 text-[10px] font-mono text-slate-400 break-all max-h-20 overflow-auto">
          {shareCode()}
        </div>
        <button
          type="button"
          onClick={copyCode}
          className="w-full py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs font-black cursor-pointer flex items-center justify-center gap-2"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copiado' : 'Copiar código'}
        </button>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={startQuick}
          className="flex-1 py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm cursor-pointer flex items-center justify-center gap-2"
        >
          <RotateCw size={16} /> Revancha
        </button>
        <button
          type="button"
          onClick={() => setPhase('menu')}
          className="flex-1 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white font-black text-sm cursor-pointer"
        >
          Menú
        </button>
      </div>
    </div>
  )
}
